import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { observer } from 'mobx-react';
import { IAdminxStore } from '../../../../modules/stores';
import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { Disable } from '@monorepo/base/src/components/disable/disable';
import { Toggle } from '@monorepo/base/src/components/form/toggle/toggle';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { Spacer, SpacerX } from '@monorepo/base/src/components/spacer/spacer';
import { sentenceCase } from 'change-case';
import styles from './edit-bidder.module.scss';
import { ErrorMsg } from '../../../../modules/components/error-msg/error-msg';
import { BidderCampaignForm } from '../../../../modules/components/forms/bidder-campaign/bidder-campaign.form';
import { Statuses } from '../../../../modules/enums/status';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';
import { useBidderCampaignGet } from '../../../../modules/hooks/apis/use-bidder-campaign-get';
import { useParams } from 'react-router-dom';
import { BidderApi } from '../../../../modules/apis/bidder.api';
import routes from '../../../../modules/routes/index.routes';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { useBidderCampaignTableOfContents } from '../../../../modules/hooks/toc/bidder-campaign.toc';
import { Nav, NavGroup, NavItem, NavPage } from '@monorepo/base/src/components/nav/nav';
import { TableOfContents } from '@monorepo/base/src/components/table-of-contents/table-of-contents';
import { ActionModal, ModalTypes } from '@monorepo/controlled/src/components/modal/modals/action-modal/action-modal';
import { Flex } from '@monorepo/base/src/components/flex/flex';
import { BidderDomainsApi } from '../../../../modules/apis/bidder-domains.api';

export const EditBidderCampaign = observer(() => {
	const { bidderStore, modalsStore, toastsStore } = useStores<IAdminxStore>();
	const cloneErrorNotifications = useRef<Record<string, boolean>>({});
	const bidderCampaignCrudStore = bidderStore.getCrud();
	const isLoading = bidderCampaignCrudStore.getIsLoading();
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const error = bidderCampaignCrudStore.getHttpError();
	const campaign = bidderCampaignCrudStore.getData().campaign;
	const originCampaign = bidderCampaignCrudStore.getOriginData().campaign;
	const isCampaignDirty =
		!!campaign && !!originCampaign && JSON.stringify(campaign.toObject()) !== JSON.stringify(originCampaign.toObject());
	const navigate = useNavigateParams();
	const { dispatchLog } = useLogs();
	const { bidderCampaignId } = useParams() as { bidderCampaignId: string };

	const { currentRoute } = useRoute();
	const [activeRoute, setActiveRoute] = useState<string>(currentRoute);
	const { tableOfContents, isTableOfContents, setIsTableOfContents } = useBidderCampaignTableOfContents();

	useBidderCampaignGet({ id: bidderCampaignId });

	useEffect(() => {
		setIsTableOfContents(true);

		return () => {
			bidderCampaignCrudStore.reset();
		};
	}, []);

	useEffect(() => {
		let toastId: number;

		if (bidderCampaignId && campaign && campaign?.note && !cloneErrorNotifications.current?.[bidderCampaignId]) {
			cloneErrorNotifications.current = {
				...cloneErrorNotifications.current,
				[bidderCampaignId]: true,
			};

			toastId = toastsStore.addToast({
				children: (
					<React.Fragment>
						<div>Duplication Failures</div>
						<div dangerouslySetInnerHTML={{ __html: campaign?.note }} />
						<Spacer />
						<Flex justifyContent="center">
							<PrimaryButton
								style={{ width: '160px' }}
								onClick={() => {
									BidderApi.ackCampaignNote(bidderCampaignId);
									toastsStore.removeToast(toastId);
								}}>
								Approve
							</PrimaryButton>
						</Flex>
					</React.Fragment>
				),
				type: DispatchLogsTypes.Error,
				disableClose: true,
				hideClose: true,
				fullHeight: true,
			});
		}

		return () => {
			if (toastId) {
				toastsStore.removeToast(toastId);
			}
		};
	}, [bidderCampaignId, campaign?.note]);

	const onEditCampaign = async () => {
		if (!bidderCampaignCrudStore.isValid()) {
			dispatchLog({
				msg: 'Form is invalid',
				type: DispatchLogsTypes.Error,
			});

			return;
		}

		setIsSaving(true);
		let hasConflicts = false;
		try {
			if (campaign.campaign_status === Statuses.Active) {
				try {
					const conflictResponse = await BidderDomainsApi.getCampaignConflictData(bidderCampaignId);
					hasConflicts = conflictResponse.campaign_conflict_data?.conflict_data?.length > 0;
				} catch (conflictError) {
					console.warn('Failed to check campaign conflicts:', conflictError);
				}
			}
			await BidderApi.updateCampaign(bidderCampaignCrudStore.getEditFormData().toObject());

			if (hasConflicts) {
				toastsStore.addToast({
					msg: `This campaign has targeting overlaps with other active campaigns. For more details, see the <span class="${styles.link}" onclick="window.location.href='${routes.domainOverlap.index()}'">Domain Overlap screen. </span>`,
					type: DispatchLogsTypes.Error,
					header: 'Campaign conflict detected',
				});
			}

			navigate(routes.bidder.campaigns());
			dispatchLog({
				msg: `Campaign ${campaign?.campaign_name} was successfully edited`,
				type: DispatchLogsTypes.Success,
			});
		} catch (e: any) {
			modalsStore.addModal({
				children: (
					<ActionModal type={ModalTypes.Error} primaryBtnTitle={'OK'}>
						<div className={styles.errorWrapper}>
							<h2 className={styles.title}>{`Failed to edit campaign.`}</h2>
							{e.data?.errors?.map((error: any) => (
								<div key={error.field} className={styles.errorContent}>{`${error.field}: ${error.message}`}</div>
							))}
						</div>
					</ActionModal>
				),
			});
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<NavPage>
			<Nav onNavClick={({ to }) => to && setActiveRoute(to)} activeRoute={activeRoute}>
				<NavItem to={`${routes.bidder.campaigns()}`}>Campaigns</NavItem>
				<Fragment>
					{isTableOfContents && (
						<NavGroup title={'Table of contents'}>
							<TableOfContents heads={tableOfContents} />
						</NavGroup>
					)}
				</Fragment>
			</Nav>
			<Page unstyled={true}>
				<Disable is={isLoading || isSaving}>
					<Page>
						<Pagebar>
							<PagebarTitle unstyled={true}>
								<div className={styles.title}>Edit a campaign</div>
								<SpacerX />
								<Toggle
									label={
										campaign.campaign_status === Statuses.Active
											? sentenceCase(Statuses.Active)
											: sentenceCase(Statuses.Paused)
									}
									onChange={status => {
										campaign.campaign_status = status ? Statuses.Active : Statuses.Paused;
									}}
									defaultValue={campaign.campaign_status === Statuses.Active}
								/>
							</PagebarTitle>
						</Pagebar>
						<BidderCampaignForm />
					</Page>
					<BottomBar>
						{error ? <ErrorMsg error={error} /> : null}
						<BottomBarActions>
							<PrimaryButton
								onClick={() => onEditCampaign()}
								disabled={isLoading || isSaving || !isCampaignDirty}
								isRotateIcon={isLoading || isSaving}
								icon={isLoading || isSaving ? 'loading-01' : 'send-03'}>
								Save
							</PrimaryButton>
						</BottomBarActions>
					</BottomBar>
				</Disable>
			</Page>
		</NavPage>
	);
});
